import { useCallback, useEffect, useMemo, useRef } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import {
    SampleDataVersion,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src";
import { experiments, getExperimentWithExposures } from "@/utils/experiment";

import { useSampleProjectConfiguration } from "../utils/sample-project";

import { useCloudStatus } from "./use-cloud-status";
import { useCloudStatusRedirection } from "./use-cloud-status-redirection";
import { useGupsStatus } from "./use-gups";

export const useCreateSampleProject = () => {
    const { cloudId } = useCloudStatus();
    const stargateClient = useBxpWebStargateClient();
    const { teamType, projectDetails, sampleProjectConfiguration } =
        useSampleProjectConfiguration();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { addMatureExampleProjectIdsCallback } = useGupsStatus();

    const createSampleProject = useCallback(async () => {
        try {
            const { projectId } = await stargateClient.sampleProjectCreate({
                cloudId,
                teamType: teamType,
                template: projectDetails.template,
                issueTypes: sampleProjectConfiguration.issueTypes,
                statusNames: sampleProjectConfiguration.columns,
                customDetail: {
                    sampleDataVersion: SampleDataVersion.MATURE,
                    enableExtraFields: true
                }
            });
            if (projectId !== "") {
                await addMatureExampleProjectIdsCallback([projectId]);

                const promises: Promise<boolean>[] = [];

                const putSampleProjectUserPropertyPromise = stargateClient
                    .putSampleProjectUserProperties({
                        projectId,
                        tenantId: cloudId,
                        template: projectDetails.template
                    })
                    .catch((error) => {
                        createAnalyticsEvent({
                            eventType: "track",
                            action: "failed",
                            actionSubject: "sampleProjectUserProperty",
                            error
                        }).fire();
                        return false;
                    });

                promises.push(putSampleProjectUserPropertyPromise);

                await Promise.all(promises);

                createAnalyticsEvent({
                    eventType: "track",
                    action: "created",
                    actionSubject: "createSampleProject"
                }).fire();

                return projectId;
            }
        } catch (error) {
            console.error("Error creating sample project:", error);
            createAnalyticsEvent({
                eventType: "track",
                action: "failed",
                actionSubject: "createSampleProject",
                error
            }).fire();
        }

        return undefined;
    }, [
        stargateClient,
        cloudId,
        teamType,
        projectDetails,
        sampleProjectConfiguration
    ]);

    return { createSampleProject };
};

/**
 * Hook to handle creating a mature sample project based on experiment enrollment.
 */
export const useMatureSampleProjectWithExperiment = (
    isEligibleForMatureSampleProject: boolean
) => {
    const { addBlockingRedirectPromise } = useCloudStatusRedirection();
    const createSampleProjectRequestInitialized = useRef(false);
    const { createSampleProject } = useCreateSampleProject();
    const { createCloudStatus } = useCloudStatus();

    const isSampleProjectVariation = useMemo(() => {
        if (!isEligibleForMatureSampleProject) {
            return false;
        }

        const sampleProjectExperiment = getExperimentWithExposures(
            experiments.jfindMatureExampleProjectExperiment
        );

        return (
            sampleProjectExperiment.type === "enrolled" &&
            sampleProjectExperiment.value
        );
    }, [isEligibleForMatureSampleProject]);

    useEffect(() => {
        if (
            isSampleProjectVariation &&
            createCloudStatus === "success" &&
            createSampleProjectRequestInitialized.current === false
        ) {
            createSampleProjectRequestInitialized.current = true;
            const sampleProjectPromise = createSampleProject();
            addBlockingRedirectPromise(sampleProjectPromise);
        }
    }, [
        isSampleProjectVariation,
        createCloudStatus,
        addBlockingRedirectPromise,
        createSampleProject
    ]);
};
